import { Filter, MoreHorizCircle } from "iconoir-react";
import React, { memo, useEffect, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { inject, observer } from "mobx-react";
import moment from "moment";
import { DATE_FORMAT_DEFAULT, TYPE_STATUS } from "../../utils/constants";
import {
    EmptyText,
    trimOverlengthString,
} from "../../components/common/CellText";
import TablePagination from "@mui/material/TablePagination";
import { useLocation, useNavigate, withRouter } from "react-router-dom";
import queryString from "query-string";
import { getNameInCapitalize, TextWrap } from "../../utils/common";
import { StatusTag } from "../../components/common/StatusTag";
import SkeletonTable from "../../components/Skeleton/SkeletonTable";
import EmptyData from "../../components/image/EmptyData";
import UnreadText from "../../components/common/UnreadText";
import ContentModal from "../../components/dialog/ContentModal";

const checkUnread = (record) => record.status_doc_outgoing !== "READ";

function createData(
    outgoing_date,
    document_number,
    title,
    authority_issued,
    signer,
    code
) {
    return {
        outgoing_date,
        document_number,
        title,
        authority_issued,
        signer,
        code,
    };
}

const InternalDocumentOutgoing = ({ internalDocumentStore }) => {
    const {
        internalDocumentOutgoingList,
        internalDocumentOutgoingListTotalCount,
        getInternalDocumentOutgoingList,
    } = internalDocumentStore;

    const location = useLocation();
    const navigate = useNavigate();
    const queryStringParse = queryString.parse(location.search, {
        arrayFormat: "index",
    });

    const [filter, setFilter] = useState({ page: 0, size: 10, title: null });
    const [searchValue, setSearchValue] = useState();
    const [isRefreshPage, setIsRefreshPage] = useState(true);
    const [loading, setLoading] = useState(true);
    const [openContent, setOpenContent] = useState(false);
    const [workSelect, setWorkSelect] = useState({});

    const NGAY_DI_WIDTH = 100;
    const SO_HIEU_WIDTH = 150;
    const TRICH_YEU_WIDTH = 400;
    const CO_QUAN_BAN_HANH_WIDTH = 200;
    const NGUOI_KY_WIDTH = 150;
    const SCROLL_WIDTH = 1000;

    useEffect(() => {
        const param = {
            page: queryStringParse.page || filter.page,
            size: queryStringParse.size || filter.size,
            title: queryStringParse.title || filter.title,
        };
        setFilter(param);
        setSearchValue(queryStringParse.title || "");
        (async () => {
            setLoading(true);
            try {
                await Promise.all([getInternalDocumentOutgoingList(param)]);
                setIsRefreshPage(false);
            } catch (err) {
                console.log(err);
            } finally {
                setLoading(false);
            }
        })();
        return () => {
            internalDocumentStore.clearInternalDocumentOutgoingList();
        };
    }, []);
    useEffect(() => {
        if (isRefreshPage) return;
        const queryStringStringify = queryString.stringify(filter, {
            skipNull: true,
        });
        navigate({
            pathname: "/internal-document/outgoing-document",
            search: queryStringStringify,
        });
        (async () => {
            setLoading(true);
            try {
                await getInternalDocumentOutgoingList(filter);
            } catch (err) {
                console.log(err);
            } finally {
                setLoading(false);
            }
        })();
    }, [filter]);
    const handleQuickSearch = () => {
        setFilter({ ...filter, page: 0, title: searchValue });
    };

    const handleChangeRowsPerPage = (event) => {
        setFilter({
            ...filter,
            page: 0,
            size: parseInt(event.target.value, 10),
        });
    };
    const onChangePagination = (event, pageIndex) => {
        setFilter({ ...filter, page: +pageIndex });
    };
    const rows = internalDocumentOutgoingList.map((record) =>
        createData(
            <UnreadText
                text={moment(record.outgoing_date).format(DATE_FORMAT_DEFAULT)}
                isUnread={checkUnread(record)}
                width={NGAY_DI_WIDTH}
            />,
            <UnreadText
                text={record.document_number}
                isUnread={checkUnread(record)}
                width={SO_HIEU_WIDTH}
            />,
            <UnreadText
                text={record.title}
                isUnread={checkUnread(record)}
                width={TRICH_YEU_WIDTH}
                icon={
                    <MoreHorizCircle
                        className="text-primary"
                        onClick={(e) => {
                            e.stopPropagation();
                            setOpenContent(true);
                            setWorkSelect(record);
                        }}
                    />
                }
            />,
            <UnreadText
                text={record.authority_issued}
                isUnread={checkUnread(record)}
                width={CO_QUAN_BAN_HANH_WIDTH}
            />,
            record.signer ? (
                <UnreadText
                    text={record.signer}
                    isUnread={checkUnread(record)}
                    width={NGUOI_KY_WIDTH}
                />
            ) : (
                <EmptyText>Không có.</EmptyText>
            ),
            record.code
        )
    );

    return (
        <>
            <input
                type="text"
                placeholder="Tìm kiếm"
                className="bg-[#F8F8F8] mt-2 px-5 py-4 rounded-xl w-full  focus-visible:outline-none"
                onChange={(e) => {
                    setSearchValue(e.target.value);
                }}
                value={searchValue}
                onSubmit={handleQuickSearch}
                onBlur={handleQuickSearch}
                onKeyPress={(e) => {
                    if (e.key === "Enter") {
                        handleQuickSearch();
                    }
                }}
            />
            {loading ? (
                <SkeletonTable />
            ) : internalDocumentOutgoingList?.length > 0 ? (
                <>
                    <section className="mt-5 overflow-x-auto hideScrollbar">
                        <TableContainer
                            component={Paper}
                            className="inline-block w-auto overflow-visible hideScrollbar shadow-none border-[1px] border-[rgba(224,224,224,1)] rounded-lg"
                        >
                            <Table
                                sx={{ width: SCROLL_WIDTH }}
                                aria-label="simple table"
                                className=""
                            >
                                <TableHead className=" bg-primary">
                                    <TableRow>
                                        <TableCell
                                            sx={{
                                                width: NGAY_DI_WIDTH,
                                            }}
                                            className="font-bold text-white rounded-tl-lg "
                                        >
                                            Ngày đi
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                width: SO_HIEU_WIDTH,
                                            }}
                                            className="font-bold text-white "
                                        >
                                            Số hiệu
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                width: TRICH_YEU_WIDTH,
                                            }}
                                            className="font-bold text-white "
                                        >
                                            Trích yếu
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                width: CO_QUAN_BAN_HANH_WIDTH,
                                            }}
                                            className="font-bold text-white "
                                        >
                                            Cơ quan ban hành
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                width: NGUOI_KY_WIDTH,
                                            }}
                                            className="font-bold text-white rounded-tr-lg "
                                        >
                                            Người ký
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map((row) => (
                                        <TableRow
                                            onClick={() => {
                                                navigate(
                                                    `/internal-document/outgoing-document/view/${row.code}`
                                                );
                                            }}
                                            key={row.code}
                                            sx={{
                                                "&:last-child td, &:last-child th":
                                                    {
                                                        border: 0,
                                                    },
                                            }}
                                        >
                                            <TableCell
                                                component="th"
                                                scope="row"
                                            >
                                                {row.outgoing_date}
                                            </TableCell>
                                            <TableCell>
                                                {row.document_number}
                                            </TableCell>
                                            <TableCell>{row.title}</TableCell>
                                            <TableCell>
                                                {row.authority_issued}
                                            </TableCell>
                                            <TableCell>{row.signer}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </section>
                    <TablePagination
                        component="div"
                        count={internalDocumentOutgoingListTotalCount}
                        page={+filter.page}
                        onPageChange={onChangePagination}
                        rowsPerPage={+filter.size}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage="Số bản ghi"
                    />
                </>
            ) : (
                <EmptyData />
            )}
            <ContentModal
                open={openContent}
                handleClose={() => {
                    setOpenContent(false);
                }}
                content={workSelect?.title}
                headerTitle="Văn bản đi"
            />
        </>
    );
};

export default memo(
    inject("internalDocumentStore")(observer(InternalDocumentOutgoing))
);
